/**
 * Firma oluşturma formu — gönderilecek veri ve telefon kuralı (#261).
 *
 * Boş e-posta / telefon için sunucunun beklediği varsayılanlar YALNIZ gönderilen
 * veride uygulanır; form state'ine yazılmaz (önceden inputa yazılıyordu, onay
 * ekranından "Geri Dön" deyince kutularda sahte değer kalıyordu).
 */

// Telefon kuralı muhataplarla ortak (#367) — tek kaynak firmContacts
import { FIRM_PHONE_PATTERN, cleanContacts, validateContacts } from "./firmContacts";

export { FIRM_PHONE_PATTERN };

export const DEFAULT_FIRM_EMAIL = "firma@example.com";
export const DEFAULT_FIRM_TEL = "+01111111111";
const PLACEHOLDER_TAX_VALUE = "1111111111";

/** Boş telefon serbest (varsayılan atanır); doluysa sunucu kuralına uymalı */
export function isValidFirmPhone(tel) {
  const value = String(tel || "").trim();
  return value === "" || FIRM_PHONE_PATTERN.test(value);
}

/**
 * Form state'inden sunucuya gidecek gövde. Form state'i değişmez.
 * @param {object} input
 */
export function buildFirmCreatePayload(input) {
  const payload = {
    ...input,
    ownerName: (input.ownerName || "").trim(),
    // Muhataplar (#367) — boş satırlar atılır
    contacts: cleanContacts(input.contacts),
    email: (input.email || "").trim() || DEFAULT_FIRM_EMAIL,
    tel: (input.tel || "").trim() || DEFAULT_FIRM_TEL,
  };
  if (!payload.mainFirmName) payload.mainFirmName = "none";
  // Üst firma bayi ise (#373): yalnız id gider, ana firma gönderilmez
  delete payload.dealerFirm;
  if (input.dealerFirm?.id) {
    payload.dealerFirmId = String(input.dealerFirm.id);
    payload.mainFirmName = "none";
  }
  // Ürün listesi alınamadıysa alan gönderilmez → sunucu hepsini açık başlatır (#275)
  if (!Array.isArray(payload.activeProducts)) delete payload.activeProducts;
  if (!payload.isCorporate) {
    payload.taxNumber = PLACEHOLDER_TAX_VALUE;
    payload.taxOffice = PLACEHOLDER_TAX_VALUE;
    payload.officialNumber = PLACEHOLDER_TAX_VALUE;
  }
  return payload;
}

/** Firma oluşturma sihirbazının adımları (#275) */
export const FIRM_CREATE_STEPS = ["Firma", "İletişim", "Ürünler", "Ayarlar ve Onay"];

/**
 * Bir adımın alan hataları — boş obje = adım geçerli. "İleri" yalnız o adımı
 * doğrular; son adım tüm formu doğrular (createFirm öncesi).
 * @param {number} step  1 tabanlı adım
 * @param {object} input form state'i
 * @param {{ productsAvailable?: boolean }} [opts] ürün listesi alınamadıysa
 *   ürün adımı engellenmez (sunucu hepsini açık başlatır)
 */
export function validateFirmCreateStep(step, input, { productsAvailable = true } = {}) {
  const errors = {};
  if (step === 1) {
    if (!input.firmName.trim()) errors.firmName = "Firma adı zorunludur";
  }
  if (step === 2) {
    if (input.email.trim() && !/\S+@\S+\.\S+/.test(input.email)) {
      errors.email = "Geçerli bir email adresi giriniz";
    }
    // Sunucuyla aynı kural — hata onaydan sonra değil, kutunun altında (#261)
    if (!isValidFirmPhone(input.tel)) {
      errors.tel = "Geçerli bir telefon giriniz (7-20 karakter; rakam, boşluk, +, (, ), -)";
    }
    if (input.isCorporate === true) {
      if (!input.taxNumber.trim()) errors.taxNumber = "Vergi numarası zorunludur";
      if (!input.taxOffice.trim()) errors.taxOffice = "Vergi dairesi zorunludur";
      if (!input.officialNumber.trim()) errors.officialNumber = "Resmi ID zorunludur";
    }
    // Muhatap satırları (#367) — hata satırın kendi kutusunun altında
    const contactErrors = validateContacts(input.contacts);
    if (Object.keys(contactErrors).length) errors.contacts = contactErrors;
    if (input.address.text.trim() === "") errors.addressText = "Adres zorunludur";
    if (input.address.city.trim() === "") errors.addressCity = "Şehir zorunludur";
    if (input.address.town.trim() === "") errors.addressTown = "İlçe zorunludur";
  }
  if (step === 3 && productsAvailable) {
    if (!Array.isArray(input.activeProducts) || input.activeProducts.length === 0) {
      errors.activeProducts = "En az bir aktif ürün seçmelisiniz";
    }
  }
  if (step === 4) {
    const days = Number(input.orderFrequencyDays);
    if (!(days >= 1 && days <= 360)) {
      errors.orderFrequencyDays = "Sipariş sıklığı 1-360 gün arasında olmalıdır";
    }
  }
  return errors;
}

/** Hatası olan ilk adım (1 tabanlı) — yoksa null. Onayda geri dönülecek adım. */
export function firstInvalidFirmCreateStep(input, opts) {
  for (let step = 1; step <= FIRM_CREATE_STEPS.length; step += 1) {
    if (Object.keys(validateFirmCreateStep(step, input, opts)).length) return step;
  }
  return null;
}
